import { useState, useEffect } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { useProcurement } from "@/context/ProcurementContext";
import { formatCurrency } from "@/lib/utils";
import { 
  InvoiceList, 
  InvoiceForm, 
  InvoiceDetails 
} from "@/components/invoice";
import { InvoiceVerificationModal } from "@/components/invoice/InvoiceVerificationModal";
import { Invoice } from "@/data/mockData";
import { DebitNotesForm } from '@/components/debitnotes';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Plus, 
  FileText, 
  BarChart3, 
  List,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Package,
  Star,
  Eye
} from "lucide-react";
import { toast } from "sonner";

const InvoiceManagement = () => {
  const { state, actions } = useProcurement();
  const [activeTab, setActiveTab] = useState("invoices");
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<number | null>(null);
  const [editingInvoice, setEditingInvoice] = useState<number | null>(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verifyingInvoice, setVerifyingInvoice] = useState<number | null>(null);
  const [vendorFilter, setVendorFilter] = useState<number | null>(null);
  const [showDebitNotesForm, setShowDebitNotesForm] = useState(false);
  const [selectedInvoiceForDebitNote, setSelectedInvoiceForDebitNote] = useState<any>(null);

  useEffect(() => {
    actions.fetchInvoices();
    
    // Check for vendor filter in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const vendorId = urlParams.get('vendor');
    if (vendorId) {
      setVendorFilter(parseInt(vendorId));
    }
  }, []);

  // Filter invoices based on vendor filter and sort by creation date (newest first)
  const filteredInvoices = (vendorFilter 
    ? (state.invoices || []).filter(invoice => invoice.vendorId === vendorFilter)
    : (state.invoices || [])).sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

  // Filter invoices for Quality Control (Created status and not quality tested)
  const qualityControlInvoices = (vendorFilter 
    ? (state.invoices || []).filter(invoice => 
        invoice.vendorId === vendorFilter && 
        invoice.status === 'Created' && 
        !invoice.qualityTested
      )
    : (state.invoices || []).filter(invoice => 
        invoice.status === 'Created' && 
        !invoice.qualityTested
      )).sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

  const handleCreateInvoice = () => {
    setEditingInvoice(null);
    setShowForm(true);
  };

  const handleEditInvoice = (id: number) => {
    setEditingInvoice(id);
    setShowForm(true);
  };

  const handleViewInvoice = (id: number) => {
    setSelectedInvoice(id);
    setShowDetails(true);
  };

  const handleApproveInvoice = async (id: number) => {
    try {
      const success = await actions.updateInvoice(id, { status: "Approved" });
      if (success) {
        toast.success("Invoice approved successfully");
      }
    } catch (error) {
      console.error("Failed to approve invoice:", error);
      toast.error("Failed to approve invoice");
    }
  };

  const handleRejectInvoice = async (id: number) => {
    try {
      const success = await actions.updateInvoice(id, { status: "Rejected" });
      if (success) {
        toast.success("Invoice rejected");
      }
    } catch (error) {
      console.error("Failed to reject invoice:", error);
      toast.error("Failed to reject invoice");
    }
  };

  const handleDeleteInvoice = async (id: number) => {
    try {
      const success = await actions.deleteInvoice(id);
      if (success) {
        toast.success("Invoice deleted successfully");
      }
    } catch (error) {
      console.error("Failed to delete invoice:", error);
      toast.error("Failed to delete invoice");
    }
  };

  const handleSaveInvoice = async (invoiceData: Partial<Invoice>): Promise<boolean> => {
    try {
      if (editingInvoice) {
        // Update existing invoice
        const success = await actions.updateInvoice(editingInvoice, invoiceData);
        if (success) {
          // Update PO status based on received quantities
          await updatePOStatusBasedOnInvoice(editingInvoice, invoiceData);
          toast.success("Invoice updated successfully");
          setShowForm(false);
          setEditingInvoice(null);
          return true;
        }
        return false;
      } else {
        // Create new invoice
        const success = await actions.createInvoice(invoiceData);
        if (success) {
          // Update PO status based on quantity logic
          if (invoiceData.poNumber && (invoiceData as any)._shouldUpdatePOStatus) {
            await updatePOStatusAfterInvoiceCreation(invoiceData.poNumber, invoiceData);
          } else if (invoiceData.poNumber) {
            // Fallback to CREATED status
            await updatePOStatusAfterInvoiceCreation(invoiceData.poNumber);
          }
          toast.success("Invoice created successfully");
          setShowForm(false);
          return true;
        }
        return false;
      }
    } catch (error) {
      console.error("Failed to save invoice:", error);
      toast.error("Failed to save invoice");
      return false;
    }
  };

  // Update PO status based on received quantities
  const updatePOStatusBasedOnInvoice = async (invoiceId: number, invoiceData: Partial<Invoice>) => {
    try {
      const invoice = state.invoices.find(inv => inv.id === invoiceId);
      if (!invoice) return;

      const relatedPO = state.purchaseOrders.find(po => po.poNumber === invoice.poNumber);
      if (!relatedPO) return;

      // Use the metadata from invoice creation if available
      if ((invoiceData as any)._shouldUpdatePOStatus && (invoiceData as any)._newPOStatus) {
        const newPOStatus = (invoiceData as any)._newPOStatus;
        
        // Update PO status
        await actions.updatePurchaseOrder(relatedPO.id, {
          status: newPOStatus,
          updatedAt: new Date().toISOString()
        });
        
        console.log(`PO ${relatedPO.poNumber} status updated to ${newPOStatus} based on invoice creation`);
        console.log(`Total Received: ${(invoiceData as any)._totalReceivedForPO}, PO Quantity: ${(invoiceData as any)._totalPOQuantity}`);
        return;
      }

      // Fallback to calculation method for existing invoices
      // Calculate total received quantity across all invoices for this PO
      const allInvoicesForPO = state.invoices.filter(inv => inv.poNumber === relatedPO.poNumber);
      const totalInvoicedQuantity = allInvoicesForPO.reduce((sum, inv) => sum + (inv.receivedQuantity || 0), 0);
      
      // Calculate PO total quantity
      const poTotalQuantity = relatedPO.items.reduce((sum, item) => sum + item.quantity, 0);

      let newPOStatus = relatedPO.status;

      // Implement your exact requirements:
      // - if PO_quantity = current_invoiced_quantity + summation of previous invoiced quantity with this PO_id then PO_status = FULLY_RECEIVED
      // - if PO_quantity > current_invoiced_quantity + summation of previous invoiced quantity with this PO_id then PO_status = PARTLY_RECEIVED
      // - if PO_quantity < current_invoiced_quantity + summation of previous invoiced quantity with this PO_id then PO_status = OVER_INVOICED
      if (totalInvoicedQuantity === poTotalQuantity) {
        newPOStatus = 'FULLY_RECEIVED';
      } else if (totalInvoicedQuantity < poTotalQuantity) {
        newPOStatus = 'PARTLY_RECEIVED';
      } else if (totalInvoicedQuantity > poTotalQuantity) {
        newPOStatus = 'OVER_INVOICED';
      }

      // Update PO status if it changed
      if (newPOStatus !== relatedPO.status) {
        await actions.updatePurchaseOrder(relatedPO.id, {
          status: newPOStatus,
          updatedAt: new Date().toISOString()
        });
        console.log(`PO ${relatedPO.poNumber} status updated to ${newPOStatus}`);
        console.log(`Total Received: ${totalInvoicedQuantity}, PO Quantity: ${poTotalQuantity}`);
      }
    } catch (error) {
      console.error('Failed to update PO status:', error);
    }
  };

  // Update PO status after invoice creation based on quantity logic
  const updatePOStatusAfterInvoiceCreation = async (poNumber: string, invoiceData?: Partial<Invoice>) => {
    try {
      const relatedPO = state.purchaseOrders.find(po => po.poNumber === poNumber);
      if (!relatedPO) return;

      // If invoice data contains PO status update metadata, use it
      if (invoiceData && (invoiceData as any)._shouldUpdatePOStatus && (invoiceData as any)._newPOStatus) {
        await actions.updatePurchaseOrder(relatedPO.id, {
          status: (invoiceData as any)._newPOStatus,
          updatedAt: new Date().toISOString()
        });
        console.log(`PO ${poNumber} status updated to ${(invoiceData as any)._newPOStatus} after invoice creation`);
        console.log(`Logic: Total Received: ${(invoiceData as any)._totalReceivedForPO}, PO Quantity: ${(invoiceData as any)._totalPOQuantity}`);
        return;
      }

      // Fallback: Set PO status to CREATED for new invoices without specific logic
      await actions.updatePurchaseOrder(relatedPO.id, {
        status: 'CREATED',
        updatedAt: new Date().toISOString()
      });
      console.log(`PO ${poNumber} status set to CREATED after invoice creation`);
    } catch (error) {
      console.error('Failed to update PO status after invoice creation:', error);
    }
  };

  // Function to update existing invoices to "Created" status for IQC Ready
  const updateInvoicesToCreatedStatus = async () => {
    try {
      const invoicesToUpdate = state.invoices.filter(invoice => 
        invoice.receivedStatus === 'Received' || 
        invoice.status === 'Pending'
      );

      if (invoicesToUpdate.length === 0) {
        toast.info('No invoices need status update');
        return;
      }

      console.log(`Found ${invoicesToUpdate.length} invoices to update to "Created" status`);

      for (const invoice of invoicesToUpdate) {
        await actions.updateInvoice(invoice.id, {
          status: 'Created',
          qualityTested: false,
          updatedAt: new Date().toISOString()
        });
        console.log(`Updated invoice ${invoice.invoiceNumber} to "Created" status`);
      }

      toast.success(`Updated ${invoicesToUpdate.length} invoices to "Created" status`);
      
      // Refresh data
      actions.fetchInvoices();
      actions.fetchPurchaseOrders();
    } catch (error) {
      console.error('Failed to update invoices to Created status:', error);
      toast.error('Failed to update invoice statuses');
    }
  };

  // Function to update PO statuses based on quantity calculations
  const updatePOStatusesBasedOnQuantities = async () => {
    try {
      let updatedPOs = 0;
      const updateLog: string[] = [];

      // Process each PO
      for (const po of state.purchaseOrders) {
        // Skip if PO has no items
        if (!po.items || po.items.length === 0) {
          console.warn(`PO ${po.poNumber} has no items, skipping`);
          continue;
        }

        // Find all invoices for this PO
        const poInvoices = state.invoices.filter(invoice => invoice.poNumber === po.poNumber);
        
        if (poInvoices.length === 0) {
          console.log(`PO ${po.poNumber} has no invoices, skipping`);
          continue;
        }

        // Calculate total invoiced quantity for this PO
        const totalInvoicedQuantity = poInvoices.reduce((sum, invoice) => {
          return sum + (invoice.invoicedQuantity || 0);
        }, 0);

        // Calculate PO total quantity
        const poTotalQuantity = po.items.reduce((sum, item) => sum + (item.quantity || 0), 0);

        // Skip if quantities are invalid
        if (isNaN(totalInvoicedQuantity) || isNaN(poTotalQuantity)) {
          console.warn(`PO ${po.poNumber} has invalid quantities, skipping`);
          continue;
        }

        // Determine new PO status based on quantity comparison
        let newPOStatus = '';
        if (totalInvoicedQuantity === poTotalQuantity) {
          newPOStatus = 'FULLY_RECEIVED';
        } else if (totalInvoicedQuantity < poTotalQuantity) {
          newPOStatus = 'PARTLY_RECEIVED';
        } else if (totalInvoicedQuantity > poTotalQuantity) {
          newPOStatus = 'OVER_INVOICED';
        } else {
          // Fallback case - should not reach here with current logic
          newPOStatus = 'CREATED';
        }

        // Update PO status if it changed
        if (newPOStatus !== po.status) {
          await actions.updatePurchaseOrder(po.id, {
            status: newPOStatus,
            updatedAt: new Date().toISOString()
          });

          updatedPOs++;
          updateLog.push(`${po.poNumber}: ${po.status} → ${newPOStatus} (Invoiced: ${totalInvoicedQuantity}, PO Qty: ${poTotalQuantity})`);
          console.log(`PO ${po.poNumber} status updated to ${newPOStatus}`);
        }
      }

      if (updatedPOs === 0) {
        toast.info('All PO statuses are already up to date');
        return;
      }

      toast.success(`Updated ${updatedPOs} PO statuses based on quantity calculations`);
      console.log('PO Status Updates:', updateLog);
      
      // Refresh data
      actions.fetchPurchaseOrders();
    } catch (error) {
      console.error('Failed to update PO statuses:', error);
      toast.error('Failed to update PO statuses');
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingInvoice(null);
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedInvoice(null);
  };

  const handleEditFromDetails = () => {
    if (selectedInvoice) {
      setEditingInvoice(selectedInvoice);
      setShowDetails(false);
      setShowForm(true);
    }
  };

  const handleVerifyInvoice = (id: number) => {
    setVerifyingInvoice(id);
    setShowVerificationModal(true);
  };

  const handleInvoiceVerification = async (invoiceId: number, verified: boolean, notes?: string) => {
    try {
      if (verified) {
        // Update invoice status to verified
        await actions.updateInvoice(invoiceId, { 
          status: "Verified",
          verificationNotes: notes 
        });
        
        // Find the related PO and update its status to IQC Ready
        const invoice = state.invoices.find(i => i.id === invoiceId);
        if (invoice) {
          const relatedPO = state.purchaseOrders.find(po => po.id === invoice.poId);
          if (relatedPO) {
            await actions.updatePurchaseOrder(relatedPO.id, {
              status: "APPROVED",
              securityVerified: true,
              securityVerifiedBy: 1, // Current user ID
              securityVerifiedAt: new Date().toISOString()
            });
          }
        }
        
        toast.success("Invoice verified successfully. PO status updated to IQC Ready.");
      } else {
        // Reject invoice
        await actions.updateInvoice(invoiceId, { 
          status: "Rejected",
          verificationNotes: notes 
        });
        toast.success("Invoice rejected.");
      }
      
      setShowVerificationModal(false);
      setVerifyingInvoice(null);
    } catch (error) {
      console.error("Failed to verify invoice:", error);
      toast.error("Failed to verify invoice");
    }
  };

  const handleCreateDebitNotes = (invoice: any) => {
    setSelectedInvoiceForDebitNote(invoice);
    setShowDebitNotesForm(true);
  };

  const handleSaveDebitNote = async (debitNoteData: any): Promise<boolean> => {
    try {
      // Save to local storage
      const existingDebitNotes = JSON.parse(localStorage.getItem('DEBIT_NOTES') || '[]');
      const newDebitNote = {
        ...debitNoteData,
        id: Date.now(),
        createdAt: new Date().toISOString()
      };
      const updatedDebitNotes = [...existingDebitNotes, newDebitNote];
      localStorage.setItem('DEBIT_NOTES', JSON.stringify(updatedDebitNotes));

      toast.success('Debit note created successfully');
      setShowDebitNotesForm(false);
      setSelectedInvoiceForDebitNote(null);
      return true;
    } catch (error) {
      console.error('Failed to create debit note:', error);
      toast.error('Failed to create debit note');
      return false;
    }
  };

  const handleCloseDebitNotesForm = () => {
    setShowDebitNotesForm(false);
    setSelectedInvoiceForDebitNote(null);
  };

  const currentInvoice = selectedInvoice 
    ? state.invoices.find(i => i.id === selectedInvoice)
    : null;

  const editingInvoiceData = editingInvoice 
    ? state.invoices.find(i => i.id === editingInvoice)
    : null;

  // Calculate summary statistics
  const totalInvoices = state.invoices.length;
  const pendingInvoices = state.invoices.filter(i => i.status === "Pending").length;
  const approvedInvoices = state.invoices.filter(i => i.status === "Approved").length;
  const completeInvoices = state.invoices.filter(i => i.status === "Complete").length;
  const paidInvoices = state.invoices.filter(i => i.paymentStatus === "Paid").length;
  const overdueInvoices = state.invoices.filter(i => {
    if (i.paymentStatus === "Paid") return false;
    const dueDate = new Date(i.dueDate);
    const today = new Date();
    return dueDate < today;
  }).length;

  const totalAmount = state.invoices.reduce((sum, invoice) => sum + invoice.totalAmount, 0);
  const paidAmount = state.invoices
    .filter(i => i.paymentStatus === "Paid")
    .reduce((sum, invoice) => sum + invoice.totalAmount, 0);
  const pendingAmount = state.invoices
    .filter(i => i.paymentStatus === "Unpaid")
    .reduce((sum, invoice) => sum + invoice.totalAmount, 0);

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Invoice Management</h1>
            <p className="text-muted-foreground">Manage invoices and payment tracking</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const ok = await actions.refreshMockData?.();
                  if (!ok) {
                    // Fallback: reset only invoices
                    const res = await import('@/services/mockAPI').then(m => m.MockAPIService.resetInvoicesToMock());
                    if (res.success) {
                      await actions.fetchInvoices();
                      toast.success('Invoices reset to mock data');
                    } else {
                      toast.error('Failed to reset invoices');
                    }
                  }
                } catch (e) {
                  console.error(e);
                  toast.error('Failed to reset invoices');
                }
              }}
            >
              Reset
            </Button>
            <Button variant="outline" size="sm">
              <FileText className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button size="sm" onClick={handleCreateInvoice}>
              <Plus className="h-4 w-4 mr-2" />
              New Invoice
            </Button>
          </div>
        </div>

        {/* Vendor Filter Indicator */}
        {vendorFilter && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <div>
                <p className="text-sm font-medium text-blue-800">
                  Showing invoices for vendor: {state.vendors?.find(v => v.id === vendorFilter)?.name || `Vendor ${vendorFilter}`}
                </p>
                <p className="text-xs text-blue-600">
                  {filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? 's' : ''} found
                </p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setVendorFilter(null);
                // Update URL to remove vendor parameter
                const url = new URL(window.location.href);
                url.searchParams.delete('vendor');
                window.history.replaceState({}, '', url.toString());
              }}
              className="text-blue-700 border-blue-300 hover:bg-blue-100"
            >
              Clear Filter
            </Button>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Invoices</p>
                  <p className="text-2xl font-bold">{totalInvoices}</p>
                  <p className="text-xs text-muted-foreground">
                    {pendingInvoices} pending approval
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Paid Invoices</p>
                  <p className="text-2xl font-bold">{paidInvoices}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatCurrency(paidAmount)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900 rounded-lg flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Pending Payment</p>
                  <p className="text-2xl font-bold">{totalInvoices - paidInvoices}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatCurrency(pendingAmount)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Overdue</p>
                  <p className="text-2xl font-bold">{overdueInvoices}</p>
                  <p className="text-xs text-red-600">
                    Requires attention
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-9">
            <TabsTrigger value="invoices" className="flex items-center gap-2">
              <List className="h-4 w-4" />
              Invoices
            </TabsTrigger>
            <TabsTrigger value="quality-control" className="flex items-center gap-2">
              <Star className="h-4 w-4" />
              Quality Control
            </TabsTrigger>
            <TabsTrigger value="po-status" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              PO Status
            </TabsTrigger>
            <TabsTrigger value="received" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              Received
            </TabsTrigger>
            <TabsTrigger value="complete" className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Complete
            </TabsTrigger>
            <TabsTrigger value="rejected" className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Rejected
            </TabsTrigger>
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="reports" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Reports
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Invoices Tab */}
          <TabsContent value="invoices" className="space-y-4">
            <InvoiceList
              invoices={filteredInvoices as any}
              onView={handleViewInvoice}
              onEdit={handleEditInvoice}
              onDelete={handleDeleteInvoice}
              onApprove={handleApproveInvoice}
              onReject={handleRejectInvoice}
              onVerify={handleVerifyInvoice}
              onDebitNotes={handleCreateDebitNotes}
              isLoading={false}
            />
          </TabsContent>

          {/* Quality Control Tab */}
          <TabsContent value="quality-control" className="space-y-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Quality Control</h3>
                  <p className="text-sm text-muted-foreground">
                    Invoices ready for quality testing
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={updateInvoicesToCreatedStatus}
                    className="flex items-center gap-2"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Update to Created Status
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={updatePOStatusesBasedOnQuantities}
                    className="flex items-center gap-2"
                  >
                    <Package className="h-4 w-4" />
                    Update PO Status
                  </Button>
                  <Badge variant="outline" className="flex items-center gap-1">
                    <Star className="h-3 w-3" />
                    {qualityControlInvoices.length} Ready for QC
                  </Badge>
                </div>
              </div>

              {/* Quality Control Invoices */}
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="border-b">
                        <tr>
                          <th className="p-4 font-medium text-left">Invoice</th>
                          <th className="p-4 font-medium text-left">PO Number</th>
                          <th className="p-4 font-medium text-left">Vendor</th>
                          <th className="p-4 font-medium text-left">Status</th>
                          <th className="p-4 font-medium text-left">Created</th>
                          <th className="p-4 font-medium text-left">Amount</th>
                          <th className="p-4 font-medium text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {qualityControlInvoices.map((invoice) => (
                          <tr key={invoice.id} className="border-b hover:bg-muted/50">
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium">{invoice.invoiceNumber}</span>
                              </div>
                            </td>
                            <td className="p-4">
                              <Badge variant="outline">{invoice.poNumber}</Badge>
                            </td>
                            <td className="p-4">{invoice.vendorName}</td>
                            <td className="p-4">
                              <Badge variant="outline" className="flex items-center gap-1">
                                <FileText className="h-3 w-3" />
                                Created
                              </Badge>
                            </td>
                            <td className="p-4">
                              {new Date(invoice.createdAt).toLocaleDateString()}
                            </td>
                            <td className="p-4 font-medium">
                              {formatCurrency(invoice.totalAmount)}
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleViewInvoice(invoice.id)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    // Navigate to Quality Management page
                                    window.location.href = '/quality-management';
                                  }}
                                >
                                  <Star className="h-4 w-4" />
                                  QC Test
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {qualityControlInvoices.length === 0 && (
                    <div className="text-center py-8">
                      <Star className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No invoices ready for quality control</h3>
                      <p className="text-muted-foreground">
                        Invoices with "Created" status will appear here for quality testing.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* PO Status Tab */}
          <TabsContent value="po-status" className="space-y-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">PO Status Management</h3>
                  <p className="text-sm text-muted-foreground">
                    Manage PO statuses based on quantity calculations
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={updatePOStatusesBasedOnQuantities}
                    className="flex items-center gap-2"
                  >
                    <Package className="h-4 w-4" />
                    Update All PO Statuses
                  </Button>
                </div>
              </div>

              {/* PO Status Calculation Rules */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">PO Status Calculation Rules</CardTitle>
                  <CardDescription>
                    PO status is determined by comparing total invoiced quantity with PO quantity
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <h4 className="font-semibold text-green-900">Received</h4>
                      </div>
                      <p className="text-sm text-green-700">
                        <strong>Condition:</strong> PO Quantity = Total Invoiced Quantity
                      </p>
                      <p className="text-xs text-green-600 mt-1">
                        All items have been invoiced
                      </p>
                    </div>

                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                        <h4 className="font-semibold text-yellow-900">Not Received</h4>
                      </div>
                      <p className="text-sm text-yellow-700">
                        <strong>Condition:</strong> PO Quantity &gt; Total Invoiced Quantity
                      </p>
                      <p className="text-xs text-yellow-600 mt-1">
                        Some items still need to be invoiced
                      </p>
                    </div>

                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                        <h4 className="font-semibold text-red-900">Over Invoiced</h4>
                      </div>
                      <p className="text-sm text-red-700">
                        <strong>Condition:</strong> PO Quantity &lt; Total Invoiced Quantity
                      </p>
                      <p className="text-xs text-red-600 mt-1">
                        More items invoiced than ordered
                      </p>
                    </div>
                  </div>

                  {/* Example */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-2">Example Calculation</h4>
                    <div className="text-sm text-blue-700 space-y-1">
                      <p><strong>PO-2025-17:</strong> 1000 items ordered</p>
                      <p><strong>Invoice 1:</strong> 800 items invoiced → Status: <span className="font-semibold text-yellow-700">Not Received</span></p>
                      <p><strong>Invoice 2:</strong> 100 items invoiced → Status: <span className="font-semibold text-yellow-700">Not Received</span></p>
                      <p><strong>Invoice 3:</strong> 100 items invoiced → Status: <span className="font-semibold text-green-700">Received</span></p>
                      <p className="text-xs text-blue-600 mt-2">
                        Total: 800 + 100 + 100 = 1000 items (equals PO quantity)
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Current PO Statuses */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Current PO Statuses</CardTitle>
                  <CardDescription>
                    Real-time PO status based on current invoiced quantities
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {state.purchaseOrders.map((po) => {
                      // Skip if PO has no items
                      if (!po.items || po.items.length === 0) {
                        return null;
                      }

                      const poInvoices = state.invoices.filter(invoice => invoice.poNumber === po.poNumber);
                      const totalInvoicedQuantity = poInvoices.reduce((sum, invoice) => sum + (invoice.invoicedQuantity || 0), 0);
                      const poTotalQuantity = po.items.reduce((sum, item) => sum + (item.quantity || 0), 0);

                      // Skip if quantities are invalid
                      if (isNaN(totalInvoicedQuantity) || isNaN(poTotalQuantity)) {
                        return null;
                      }
                      
                      // Calculate expected status
                      let expectedStatus = '';
                      if (totalInvoicedQuantity === poTotalQuantity) {
                        expectedStatus = 'FULLY_RECEIVED';
                      } else if (totalInvoicedQuantity < poTotalQuantity) {
                        expectedStatus = 'PARTLY_RECEIVED';
                      } else if (totalInvoicedQuantity > poTotalQuantity) {
                        expectedStatus = 'OVER_INVOICED';
                      } else {
                        expectedStatus = 'CREATED';
                      }

                      const statusMatches = po.status === expectedStatus;
                      
                      return (
                        <div key={po.id} className={`flex items-center justify-between p-3 rounded-lg border ${statusMatches ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                          <div className="flex items-center gap-3">
                            <div className="font-medium">{po.poNumber}</div>
                            <div className="text-sm text-muted-foreground">
                              {poTotalQuantity} items • {totalInvoicedQuantity} invoiced
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge 
                              variant={po.status === 'FULLY_RECEIVED' ? 'default' : po.status === 'PARTLY_RECEIVED' ? 'secondary' : po.status === 'OVER_INVOICED' ? 'destructive' : 'outline'}
                            >
                              {po.status}
                            </Badge>
                            {!statusMatches && (
                              <Badge variant="outline" className="text-yellow-600 border-yellow-300">
                                Should be: {expectedStatus}
                              </Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Received Invoices Tab */}
          <TabsContent value="received" className="space-y-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Received Invoices</h3>
                  <p className="text-sm text-muted-foreground">
                    Invoices delivered by Floor Manager to Manager
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="flex items-center gap-1">
                    <Package className="h-3 w-3" />
                    {filteredInvoices.filter(inv => inv.isReceived).length} Received
                  </Badge>
                </div>
              </div>

              {/* Received Invoices Table */}
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="border-b">
                        <tr className="text-left">
                          <th className="p-4 font-medium">Invoice ID</th>
                          <th className="p-4 font-medium">PO ID</th>
                          <th className="p-4 font-medium">Vendor</th>
                          <th className="p-4 font-medium">Status</th>
                          <th className="p-4 font-medium">Created At</th>
                          <th className="p-4 font-medium">Delivered At</th>
                          <th className="p-4 font-medium">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredInvoices.filter(inv => inv.isReceived).map((invoice) => (
                          <tr key={invoice.id} className="border-b hover:bg-muted/50">
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium">{invoice.invoiceNumber}</span>
                              </div>
                            </td>
                            <td className="p-4">
                              <Badge variant="outline">{invoice.poNumber}</Badge>
                            </td>
                            <td className="p-4">{invoice.vendorName}</td>
                            <td className="p-4">
                              <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Received
                              </Badge>
                            </td>
                            <td className="p-4 text-sm text-muted-foreground">
                              {new Date(invoice.createdAt).toLocaleDateString()}
                            </td>
                            <td className="p-4 text-sm text-muted-foreground">
                              {invoice.receivedAt ? new Date(invoice.receivedAt).toLocaleDateString() : 'N/A'}
                            </td>
                            <td className="p-4 font-medium">
                              {formatCurrency(invoice.totalAmount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {filteredInvoices.filter(inv => inv.isReceived).length === 0 && (
                    <div className="text-center py-8">
                      <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No received invoices</h3>
                      <p className="text-muted-foreground">
                        Invoices will appear here once they are delivered by the Floor Manager.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Not Received Invoices (for reference) */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                    Pending Delivery
                  </CardTitle>
                  <CardDescription>
                    Invoices waiting for delivery to Manager
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {filteredInvoices.filter(inv => !inv.isReceived).slice(0, 5).map((invoice) => (
                      <div key={invoice.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="font-medium">{invoice.invoiceNumber}</p>
                            <p className="text-sm text-muted-foreground">{invoice.vendorName}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Not Received
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            {Math.floor((Date.now() - new Date(invoice.createdAt).getTime()) / (1000 * 60 * 60))}h ago
                          </span>
                        </div>
                      </div>
                    ))}
                    
                    {filteredInvoices.filter(inv => !inv.isReceived).length === 0 && (
                      <p className="text-center text-muted-foreground py-4">
                        All invoices have been delivered!
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
        </TabsContent>

        {/* Complete Invoices Tab */}
        <TabsContent value="complete" className="space-y-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Complete Invoices</h3>
                <p className="text-sm text-muted-foreground">
                  Invoices that have been fully processed and delivered to Manager
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="default" className="flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  {filteredInvoices.filter(inv => inv.status === 'Complete').length} Complete
                </Badge>
              </div>
            </div>

            {/* Complete Invoices Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b">
                      <tr className="text-left">
                        <th className="p-4 font-medium">Invoice ID</th>
                        <th className="p-4 font-medium">PO ID</th>
                        <th className="p-4 font-medium">Vendor</th>
                        <th className="p-4 font-medium">Status</th>
                        <th className="p-4 font-medium">Quality Rating</th>
                        <th className="p-4 font-medium">Completed At</th>
                        <th className="p-4 font-medium">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInvoices.filter(inv => inv.status === 'Complete').map((invoice) => (
                        <tr key={invoice.id} className="border-b hover:bg-muted/50">
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{invoice.invoiceNumber}</span>
                            </div>
                          </td>
                          <td className="p-4">
                            <Badge variant="outline">{invoice.poNumber}</Badge>
                          </td>
                          <td className="p-4">{invoice.vendorName}</td>
                          <td className="p-4">
                            <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Complete
                            </Badge>
                          </td>
                          <td className="p-4">
                            {invoice.qualityRating ? (
                              <div className="flex items-center gap-1">
                                <span className="text-sm font-medium">{invoice.qualityRating}/5</span>
                                <div className="flex">
                                  {[...Array(5)].map((_, i) => (
                                    <Star 
                                      key={i} 
                                      className={`h-3 w-3 ${i < invoice.qualityRating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
                                    />
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <span className="text-sm text-muted-foreground">N/A</span>
                            )}
                          </td>
                          <td className="p-4 text-sm text-muted-foreground">
                            {invoice.receivedAt ? new Date(invoice.receivedAt).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="p-4 font-medium">
                            {formatCurrency(invoice.totalAmount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {filteredInvoices.filter(inv => inv.status === 'Complete').length === 0 && (
                  <div className="text-center py-8">
                    <CheckCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No complete invoices</h3>
                    <p className="text-muted-foreground">
                      Invoices will appear here once they are fully processed and delivered to the Manager.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          </TabsContent>

          {/* Rejected Invoices Tab */}
          <TabsContent value="rejected" className="space-y-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Rejected Invoices</h3>
                  <p className="text-sm text-muted-foreground">
                    Invoices that have been rejected and require debit note creation
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="destructive" className="flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {filteredInvoices.filter(inv => inv.status === 'Rejected').length} Rejected
                  </Badge>
                </div>
              </div>

              {/* Rejected Invoices Table */}
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="border-b">
                        <tr className="text-left">
                          <th className="p-4 font-medium">Invoice ID</th>
                          <th className="p-4 font-medium">PO ID</th>
                          <th className="p-4 font-medium">Vendor</th>
                          <th className="p-4 font-medium">Status</th>
                          <th className="p-4 font-medium">Rejection Reason</th>
                          <th className="p-4 font-medium">Rejected At</th>
                          <th className="p-4 font-medium">Amount</th>
                          <th className="p-4 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredInvoices.filter(inv => inv.status === 'Rejected').map((invoice) => (
                          <tr key={invoice.id} className="border-b hover:bg-muted/50">
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium">{invoice.invoiceNumber}</span>
                              </div>
                            </td>
                            <td className="p-4">
                              <Badge variant="outline">{invoice.poNumber}</Badge>
                            </td>
                            <td className="p-4">{invoice.vendorName}</td>
                            <td className="p-4">
                              <Badge variant="destructive" className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                Rejected
                              </Badge>
                            </td>
                            <td className="p-4">
                              <span className="text-sm text-muted-foreground">
                                {invoice.qualityNotes || 'Quality test failed'}
                              </span>
                            </td>
                            <td className="p-4">
                              <span className="text-sm text-muted-foreground">
                                {invoice.qualityTestedAt ? new Date(invoice.qualityTestedAt).toLocaleDateString() : 'N/A'}
                              </span>
                            </td>
                            <td className="p-4 font-medium">
                              {formatCurrency(invoice.totalAmount)}
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleViewInvoice(invoice.id)}
                                  className="flex items-center gap-1"
                                >
                                  <Eye className="h-4 w-4" />
                                  View
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleCreateDebitNotes(invoice)}
                                  className="flex items-center gap-1"
                                >
                                  <DollarSign className="h-4 w-4" />
                                  Debit Notes
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {filteredInvoices.filter(inv => inv.status === 'Rejected').length === 0 && (
                    <div className="text-center py-8">
                      <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No Rejected Invoices</h3>
                      <p className="text-muted-foreground">
                        Rejected invoices will appear here for debit note creation.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Invoice Status Distribution</CardTitle>
                  <CardDescription>Current status of all invoices</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm">Approved</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{approvedInvoices}</span>
                        <div className="w-20 bg-muted rounded-full h-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full" 
                            style={{ width: `${totalInvoices > 0 ? (approvedInvoices / totalInvoices) * 100 : 0}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                        <span className="text-sm">Pending</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{pendingInvoices}</span>
                        <div className="w-20 bg-muted rounded-full h-2">
                          <div 
                            className="bg-yellow-500 h-2 rounded-full" 
                            style={{ width: `${totalInvoices > 0 ? (pendingInvoices / totalInvoices) * 100 : 0}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                        <span className="text-sm">Rejected</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {state.invoices.filter(i => i.status === "Rejected").length}
                        </span>
                        <div className="w-20 bg-muted rounded-full h-2">
                          <div 
                            className="bg-red-500 h-2 rounded-full" 
                            style={{ width: `${totalInvoices > 0 ? (state.invoices.filter(i => i.status === "Rejected").length / totalInvoices) * 100 : 0}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Payment Status</CardTitle>
                  <CardDescription>Payment status distribution</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm">Paid</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{paidInvoices}</span>
                        <div className="w-20 bg-muted rounded-full h-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full" 
                            style={{ width: `${totalInvoices > 0 ? (paidInvoices / totalInvoices) * 100 : 0}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                        <span className="text-sm">Pending</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{totalInvoices - paidInvoices}</span>
                        <div className="w-20 bg-muted rounded-full h-2">
                          <div 
                            className="bg-yellow-500 h-2 rounded-full" 
                            style={{ width: `${totalInvoices > 0 ? ((totalInvoices - paidInvoices) / totalInvoices) * 100 : 0}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                        <span className="text-sm">Overdue</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{overdueInvoices}</span>
                        <div className="w-20 bg-muted rounded-full h-2">
                          <div 
                            className="bg-red-500 h-2 rounded-full" 
                            style={{ width: `${totalInvoices > 0 ? (overdueInvoices / totalInvoices) * 100 : 0}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Financial Overview</CardTitle>
                <CardDescription>Total amounts and payment tracking</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-primary">{formatCurrency(totalAmount)}</p>
                    <p className="text-sm text-muted-foreground">Total Invoice Value</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-600">{formatCurrency(paidAmount)}</p>
                    <p className="text-sm text-muted-foreground">Amount Paid</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-yellow-600">{formatCurrency(pendingAmount)}</p>
                    <p className="text-sm text-muted-foreground">Amount Pending</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Invoice Reports</CardTitle>
                <CardDescription>Generate and view invoice reports</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                        <FileText className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Monthly Summary</h4>
                        <p className="text-sm text-muted-foreground">Invoice summary by month</p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                        <TrendingUp className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Payment Trends</h4>
                        <p className="text-sm text-muted-foreground">Payment patterns analysis</p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center">
                        <AlertTriangle className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Overdue Report</h4>
                        <p className="text-sm text-muted-foreground">Overdue invoices tracking</p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
                        <BarChart3 className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Vendor Analysis</h4>
                        <p className="text-sm text-muted-foreground">Invoice analysis by vendor</p>
                      </div>
                    </div>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Invoice Settings</CardTitle>
                <CardDescription>Configure invoice management parameters</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="font-semibold mb-3">Payment Terms</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">Default Payment Terms</p>
                        <p className="text-sm text-muted-foreground">Standard payment terms for new invoices</p>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold">Net 30</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">Overdue Threshold</p>
                        <p className="text-sm text-muted-foreground">Days after due date to mark as overdue</p>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold">7 days</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-3">Notification Settings</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">Overdue Alerts</p>
                        <p className="text-sm text-muted-foreground">Notify when invoices become overdue</p>
                      </div>
                      <div className="w-12 h-6 bg-green-500 rounded-full relative">
                        <div className="w-5 h-5 bg-white rounded-full absolute right-0.5 top-0.5"></div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">Payment Reminders</p>
                        <p className="text-sm text-muted-foreground">Send reminders before due date</p>
                      </div>
                      <div className="w-12 h-6 bg-green-500 rounded-full relative">
                        <div className="w-5 h-5 bg-white rounded-full absolute right-0.5 top-0.5"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Invoice Form Modal */}
        {showForm && (
          <InvoiceForm
            invoice={editingInvoiceData as any}
            onSave={handleSaveInvoice as any}
            onCancel={handleCloseForm}
          />
        )}

        {/* Invoice Details Modal */}
        {showDetails && currentInvoice && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-background rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <InvoiceDetails
                  invoice={currentInvoice as any}
                  allInvoices={state.invoices as any}
                  onEdit={handleEditFromDetails}
                  onClose={handleCloseDetails}
                  onApprove={() => handleApproveInvoice(currentInvoice.id)}
                  onReject={() => handleRejectInvoice(currentInvoice.id)}
                />
              </div>
            </div>
          </div>
        )}

        {/* Invoice Verification Modal */}
        {showVerificationModal && verifyingInvoice && (
          <InvoiceVerificationModal
            isOpen={showVerificationModal}
            onClose={() => {
              setShowVerificationModal(false);
              setVerifyingInvoice(null);
            }}
            invoice={state.invoices.find(i => i.id === verifyingInvoice) || null}
            onVerify={handleInvoiceVerification}
            currentUser={state.users[0]} // For now, use first user as current user
          />
        )}

        {/* Debit Notes Form Modal */}
        {showDebitNotesForm && selectedInvoiceForDebitNote && (
          <DebitNotesForm
            isOpen={showDebitNotesForm}
            onClose={handleCloseDebitNotesForm}
            onSave={handleSaveDebitNote}
            linkedInvoice={selectedInvoiceForDebitNote}
            mode="invoice"
          />
        )}
      </div>
    </AppLayout>
  );
};

export default InvoiceManagement;
